/**
 * SIATA Santa Elena radar bounds validation (client).
 */
import { SIATA_SANTA_ELENA_APPROVED_MANUAL_BOUNDS } from "../constants/siataSantaElenaCalibrationConstants.js";

export const SIATA_MAX_BOUNDS_AREA_SQ_DEG = 2.5;

export const BOUNDS_LIMITS = {
  lngMin: -180,
  lngMax: 180,
  latMin: -90,
  latMax: 90,
};

export function parseBoundsEnv(value) {
  if (!value) {
    return null;
  }

  const parts = String(value)
    .split(",")
    .map((part) => Number(part.trim()))
    .filter((part) => Number.isFinite(part));

  if (parts.length !== 4) {
    return null;
  }

  const [westLng, southLat, eastLng, northLat] = parts;
  return { westLng, southLat, eastLng, northLat };
}

export function validateRadarBounds(bounds, { allowPlaceholder = false } = {}) {
  if (!bounds || typeof bounds !== "object") {
    return { valid: false, message: "Bounds are required." };
  }

  const fields = ["westLng", "southLat", "eastLng", "northLat"];
  for (const field of fields) {
    const value = bounds[field];
    if (!Number.isFinite(value)) {
      if (allowPlaceholder) {
        return { valid: false, message: `${field} is missing.`, placeholder: true };
      }
      return { valid: false, message: `${field} must be a number.` };
    }
  }

  const { westLng, southLat, eastLng, northLat } = bounds;

  if (westLng < BOUNDS_LIMITS.lngMin || westLng > BOUNDS_LIMITS.lngMax) {
    return { valid: false, message: "westLng is outside valid longitude range." };
  }
  if (eastLng < BOUNDS_LIMITS.lngMin || eastLng > BOUNDS_LIMITS.lngMax) {
    return { valid: false, message: "eastLng is outside valid longitude range." };
  }
  if (southLat < BOUNDS_LIMITS.latMin || southLat > BOUNDS_LIMITS.latMax) {
    return { valid: false, message: "southLat is outside valid latitude range." };
  }
  if (northLat < BOUNDS_LIMITS.latMin || northLat > BOUNDS_LIMITS.latMax) {
    return { valid: false, message: "northLat is outside valid latitude range." };
  }
  if (westLng >= eastLng) {
    return { valid: false, message: "westLng must be less than eastLng." };
  }
  if (southLat >= northLat) {
    return { valid: false, message: "southLat must be less than northLat." };
  }

  const area = Math.abs(eastLng - westLng) * Math.abs(northLat - southLat);
  if (area > SIATA_MAX_BOUNDS_AREA_SQ_DEG) {
    return {
      valid: false,
      message: "Bounds area is too large for local AMVA radar coverage.",
    };
  }

  return { valid: true, message: null, area };
}

export function boundsToMapboxCoordinates(bounds) {
  const validation = validateRadarBounds(bounds);
  if (!validation.valid) {
    return null;
  }

  const { westLng, southLat, eastLng, northLat } = bounds;
  return [
    [westLng, northLat],
    [eastLng, northLat],
    [eastLng, southLat],
    [westLng, southLat],
  ];
}

export function isPointInsideBounds(point, bounds) {
  if (!point || !validateRadarBounds(bounds).valid) {
    return false;
  }

  const lng = point.lng ?? point.longitude;
  const lat = point.lat ?? point.latitude;

  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return false;
  }

  return (
    lng >= bounds.westLng &&
    lng <= bounds.eastLng &&
    lat >= bounds.southLat &&
    lat <= bounds.northLat
  );
}

export function isViewportIntersectingBounds(viewport, bounds) {
  if (!viewport || !validateRadarBounds(bounds).valid) {
    return false;
  }

  const { west, east, south, north } = viewport;
  if (![west, east, south, north].every(Number.isFinite)) {
    return false;
  }

  const separated =
    east < bounds.westLng ||
    west > bounds.eastLng ||
    north < bounds.southLat ||
    south > bounds.northLat;

  return !separated;
}

export function nudgeBounds(bounds, direction, step = 0.01) {
  const next = { ...bounds };

  switch (direction) {
    case "west":
      next.westLng -= step;
      next.eastLng -= step;
      break;
    case "east":
      next.westLng += step;
      next.eastLng += step;
      break;
    case "north":
      next.northLat += step;
      next.southLat += step;
      break;
    case "south":
      next.northLat -= step;
      next.southLat -= step;
      break;
    case "expandX":
      next.westLng -= step;
      next.eastLng += step;
      break;
    case "contractX":
      next.westLng += step;
      next.eastLng -= step;
      break;
    case "expandY":
      next.northLat += step;
      next.southLat -= step;
      break;
    case "contractY":
      next.northLat -= step;
      next.southLat += step;
      break;
    default:
      break;
  }

  return validateRadarBounds(next).valid ? next : bounds;
}

export function buildReferenceMarkersGeoJson(
  points,
  properties = {}
) {
  const list = Array.isArray(points) ? points : [];
  return {
    type: "FeatureCollection",
    features: list.map((point) => ({
      type: "Feature",
      properties: {
        id: point.id,
        name: point.name,
        purpose: point.purpose,
        ...properties,
      },
      geometry: {
        type: "Point",
        coordinates: [point.lng, point.lat],
      },
    })),
  };
}

export function getApprovedManualBounds() {
  return { ...SIATA_SANTA_ELENA_APPROVED_MANUAL_BOUNDS };
}
